// Core-100 R4 wave 2 — CORE-AC: a rollover chain re-plan is an INTENT the server carries out.
//
// The browser used to run the chain planner (supabase/functions/_shared/rolloverChainAuthority.ts) itself and
// send its rows — start, finish, OptiTime, book share — to `replan_rollover_chain_from_source`. That RPC is the
// service role's only now (migration 20270206022400). The browser states what happened; the served
// `ticket-finish-intent` function runs the one planner (supabase/functions/_shared/chainIntentServer.ts) as the
// service role, keeping this person's scheduling authority, and answers with the planner's outcome — exactly the
// shape the chain wrappers in `rolloverChain.ts` used to receive.
import { supabase } from '@/integrations/supabase/client';

export type ServerChainIntent =
  | 'chain_pushback'
  | 'chain_work_change'
  | 'chain_add_future_work'
  | 'chain_replan_from_row';

export interface ServerChainParams {
  extraWallMinutes?: number;
  floorDate?: string;
  floorMin?: number;
  mayPullEarlier?: boolean;
  requireStartAtFloor?: boolean;
  label?: string;
}

export type ServerChainOutcome =
  | { status: 'applied'; written: number; lanes: Array<{ date: string; techId: string | null }> }
  | { status: 'noop' }
  | { status: 'refused'; reason: string }
  | { status: 'conflict' };

/**
 * Ask the server to re-plan a rollover chain. Returns the planner's outcome, or null when the
 * ticket is not a multi-row chain. A transport or server failure is a refusal (nothing changed).
 */
export async function requestServerChainReplan(
  ticketId: string,
  intent: ServerChainIntent,
  params: ServerChainParams = {},
): Promise<ServerChainOutcome | null> {
  const body: Record<string, unknown> = { ticketId, intent, params };
  let data: unknown;
  let error: { message?: string } | null;
  try {
    ({ data, error } = await supabase.functions.invoke('ticket-finish-intent', { body }));
  } catch (e) {
    error = { message: e instanceof Error ? e.message : String(e) };
  }
  if (error) {
    const reason = `server: ${error.message ?? String(error)}`;
    console.error(`[ticket-finish-intent] ${intent} failed for ${ticketId}: ${reason}`);
    return { status: 'refused', reason };
  }
  const envelope = (data ?? {}) as { data?: Record<string, unknown> } & Record<string, unknown>;
  const r = (envelope.data ?? envelope) as Record<string, unknown>;
  if (!('chain' in r)) return { status: 'refused', reason: `server: unexpected answer (${String(r.outcome ?? 'none')})` };
  return (r.chain ?? null) as ServerChainOutcome | null;
}
